import EventEmitter from 'events';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import '@interlucent/pixel-stream';

import {
  PixelStreamingContext,
  type PixelStreamingStatus,
} from 'src/features/PixelStreaming/contexts/PixelStreamingContext';
import type { PixelStreamStatus } from 'src/types/interlucent';

import styles from './InterlucentContextProvider.module.scss';

export interface IInterlucentContext {
  status: PixelStreamStatus;
}

export const InterlucentContext = createContext<IInterlucentContext>({
  status: 'idle',
});

type Props = {
  appId: string;
  children: React.ReactNode;
};

export default function InterlucentContextProvider({ appId, children }: Props) {
  const ref = useRef<PixelStreamElement | null>(null);
  const [descriptor] = useState(() => new EventEmitter());
  const [dataChannelOpen, setDataChannelOpen] = useState(false);
  const [preOpenMessages, setPreOpenMessages] = useState<any[]>([]);
  const [status, setStatus] = useState<PixelStreamStatus>('idle');

  useEffect(() => {
    if (!ref.current) return;

    const admissionToken = window.env?.REACT_APP_INTERLUCENT_ADMISSION_TOKEN;
    if (admissionToken) {
      ref.current.admissionToken = admissionToken;
    }

    const statusChangeListener = (event: Event) => setStatus((event as CustomEvent<{ newStatus: PixelStreamStatus }>).detail.newStatus);
    const dataChannelOpenListener = () => setDataChannelOpen(true);
    const ueCommandResponseListener = (event: Event) => {
      const data = (event as CustomEvent).detail as { type?: string };
      if (!data?.type) return;
      descriptor.emit(data.type, data);
    };

    ref.current.addEventListener('status-change', statusChangeListener);
    ref.current.addEventListener('data-channel-open', dataChannelOpenListener);
    ref.current.addEventListener('ue-command-response', ueCommandResponseListener);

    return () => {
      ref.current?.removeEventListener('status-change', statusChangeListener);
      ref.current?.removeEventListener('data-channel-open', dataChannelOpenListener);
      ref.current?.removeEventListener('ue-command-response', ueCommandResponseListener);
    };
  }, [descriptor]);

  const emitUIInteraction = useMemo(() => {
    if (!ref.current?.sendUIInteraction) return null;
    return (value: any) => {
      if (!dataChannelOpen) {
        setPreOpenMessages((prevMessages) => [...prevMessages, value]);
      } else {
        ref.current?.sendUIInteraction(value);
      }
    };
  }, [dataChannelOpen]);

  useEffect(() => {
    if (!dataChannelOpen || !ref.current || preOpenMessages.length === 0 || !emitUIInteraction) return;
    const initialPacket = preOpenMessages.find((message) => message.type === 'initial');
    if (initialPacket) emitUIInteraction(initialPacket);
    preOpenMessages
      .filter((message) => message.type !== 'initial')
      .forEach((message) => emitUIInteraction(message));
    setPreOpenMessages([]);
  }, [dataChannelOpen, emitUIInteraction, preOpenMessages]);

  return (
    <InterlucentContext.Provider value={{ status }}>
      <PixelStreamingContext.Provider
        value={{
          emitUIInteraction,
          descriptor,
          isStreaming: status === 'streaming' || status === 'ready',
          status: mapInterlucentStatus(status),
          connect: () => {},
        }}
      >
        <div className={styles.container}>
          {children}
          <pixel-stream
            ref={ref}
            className={styles.pixelstream}
            controls
            app-id={appId}
          />
        </div>
      </PixelStreamingContext.Provider>
    </InterlucentContext.Provider>
  );
}

function mapInterlucentStatus(interlucentStatus: PixelStreamStatus): PixelStreamingStatus {
  switch (interlucentStatus) {
    case 'idle':
      return 'unknown';
    case 'connecting':
    case 'authenticating':
    case 'queued':
    case 'rendezvoused':
    case 'negotiating':
    case 'recovering':
      return 'connecting';
    case 'connected':
      return 'connected';
    case 'streaming':
    case 'ready':
      return 'ready';
    case 'interrupted':
    case 'ended':
      return 'disconnected';
    case 'failed':
      return 'error';
    default: {
      const _exhaustive: never = interlucentStatus;
      return _exhaustive;
    }
  }
}

export function useInterlucentContext() {
  const context = useContext(InterlucentContext);
  if (!context) {
    throw new Error('useInterlucentContext must be used within InterlucentContextProvider');
  }
  return context;
}
